/**
 * Client-side error reporter for Hines Time.
 *
 * Captures three sources of frontend failures and ships them to the
 * self-hosted `/api/client-errors` endpoint:
 *   1) Unhandled JS exceptions       (window.error)
 *   2) Unhandled promise rejections  (window.unhandledrejection)
 *   3) console.error calls           (preserved — still logs to the browser)
 *
 * Design notes / hard requirements:
 *   - Loops: any error originating from a fetch to /api/client-errors is
 *     IGNORED to prevent an infinite report-the-reporter cycle.
 *   - Batching: errors are queued and flushed at most once per 5 s (or
 *     immediately on `pagehide` via `navigator.sendBeacon`), so a screen full
 *     of errors doesn't DDoS the server.
 *   - Auth: if an axios `Authorization` token has been written to
 *     localStorage we forward it as Bearer so the report is attributed to the
 *     correct company/user; otherwise the report goes anonymous (signin
 *     failures need this).
 *   - Dev opt-out: in `NODE_ENV === 'development'` we DO NOT POST (keeps the
 *     network panel clean while you're coding) — but still wrap console.error
 *     so the format is identical.
 *
 * Public API:
 *   `installClientErrorReporter()`  - call once from src/index.js.
 *   `reportClientError(message, extra)` - manual report from a catch block.
 */

import axios from 'axios';

const BACKEND = window.__BACKEND_URL__ || '';
const ENDPOINT = `${BACKEND}/api/client-errors`;
const APP_VERSION = process.env.REACT_APP_VERSION || 'preview';
const DISABLED_IN_DEV = process.env.NODE_ENV === 'development';

const MAX_QUEUE = 50;
const FLUSH_MS = 5000;

const queue = [];
let flushTimer = null;
let installed = false;

const truncate = (s, n) => (typeof s === 'string' && s.length > n ? s.slice(0, n - 1) + '…' : s);

const safeStringify = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  try { return JSON.stringify(v); } catch { return String(v); }
};

const composeMessage = (args) => args.map(safeStringify).filter(Boolean).join(' | ');

const extractStack = (args) => {
  for (const a of args) {
    if (a instanceof Error && a.stack) return a.stack;
    if (a && typeof a === 'object' && typeof a.stack === 'string') return a.stack;
  }
  return new Error().stack || '';
};

const getAuthToken = () => {
  try {
    return window.localStorage?.getItem('token') || '';
  } catch {
    return '';
  }
};

const buildPayload = (severity, args, fallbackStack) => ({
  severity,
  message: truncate(composeMessage(args) || '(empty)', 1000),
  stack: truncate(extractStack(args) || fallbackStack || '', 8000),
  url: typeof window !== 'undefined' ? window.location.href : '',
  user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  app_version: APP_VERSION,
  client_ts: new Date().toISOString(),
});

const scheduleFlush = () => {
  if (flushTimer || DISABLED_IN_DEV) return;
  flushTimer = setTimeout(flush, FLUSH_MS);
};

const flush = (synchronous = false) => {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (!queue.length || DISABLED_IN_DEV) return;
  const items = queue.splice(0, queue.length);
  const body = JSON.stringify({ items });
  const token = getAuthToken();
  // Synchronous unload path: use sendBeacon (no auth headers possible, but the
  // server treats it as anon and the report is still useful).
  if (synchronous && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    } catch { /* fall through to fetch */ }
  }
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Use bare fetch (not axios) so axios interceptors can't observe / loop on this.
  fetch(ENDPOINT, { method: 'POST', headers, body, keepalive: true })
    .catch(() => { /* swallow — never report the reporter */ });
};

const enqueue = (payload) => {
  // Drop if the report itself targets our endpoint (defense-in-depth).
  if (typeof payload.url === 'string' && payload.url.includes('/api/client-errors')) return;
  if (queue.length >= MAX_QUEUE) {
    // When saturated, keep the newest — old errors of the same burst are usually duplicates.
    queue.shift();
  }
  queue.push(payload);
  scheduleFlush();
};

export const reportClientError = (message, extra) => {
  enqueue({
    ...buildPayload('error', [message]),
    extra: extra && typeof extra === 'object' ? extra : undefined,
  });
};

export const installClientErrorReporter = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1) Unhandled JS exceptions
  window.addEventListener('error', (e) => {
    enqueue(buildPayload('error', [e?.error || e?.message || 'Unhandled error'], e?.error?.stack));
  });

  // 2) Unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    enqueue(buildPayload('error', [reason || 'Unhandled rejection'], reason?.stack));
  });

  // 3) console.error wrapper (preserves browser DevTools output)
  const orig = console.error.bind(console);
  console.error = (...args) => {
    try {
      // Skip our own diagnostic prints — they don't represent product bugs.
      // (Heuristic: if the first arg is a string AND contains '[ce-reporter]'.)
      const first = args[0];
      if (typeof first === 'string' && first.includes('[ce-reporter]')) {
        orig(...args);
        return;
      }
      enqueue(buildPayload('error', args));
    } catch { /* never throw from console.error */ }
    orig(...args);
  };

  // 4) Flush on page hide so reports aren't lost when the user navigates away.
  window.addEventListener('pagehide', () => flush(true));
  window.addEventListener('beforeunload', () => flush(true));

  // 5) Failed saves (Aug 2026 Error Watchdog): report failed write requests
  // so Platform HQ knows a user hit a failed save even when the UI showed a
  // friendly toast. 5xx / network → "error"; 400/422 rejections → "warn".
  try {
    axios.interceptors.response.use(
      (res) => res,
      (error) => {
        try {
          const cfg = error?.config || {};
          const method = (cfg.method || '').toLowerCase();
          const rawUrl = String(cfg.url || '');
          const endpoint = rawUrl.split('?')[0].slice(0, 300); // strip presigned/query secrets
          const status = error?.response?.status;
          const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
          const skip = endpoint.includes('/client-errors') || endpoint.includes('/auth/');
          if (isWrite && !skip) {
            if (!status || status >= 500) {
              enqueue({
                ...buildPayload('error', [`Failed save: ${method.toUpperCase()} ${endpoint} → ${status || 'network error'}`]),
                extra: { kind: 'failed_save', status: status || 0, method, endpoint },
              });
            } else if (status === 400 || status === 422) {
              let detail = '';
              try { detail = JSON.stringify(error?.response?.data?.detail ?? '').slice(0, 300); } catch { /* ignore */ }
              enqueue({
                ...buildPayload('warn', [`Rejected save: ${method.toUpperCase()} ${endpoint} → ${status} ${detail}`]),
                extra: { kind: 'rejected_save', status, method, endpoint },
              });
            }
          }
        } catch { /* the watchdog must never break the app */ }
        return Promise.reject(error);
      },
    );
  } catch { /* ignore */ }
};
